import { Component } from '@theme/component';

/**
 * Image gallery — a grid of image blocks paged client-side.
 *
 * The gallery's children are theme blocks, so there is no `paginate` object to
 * work from: the count is only known in the browser. This component chunks the
 * grid's element children into pages and rebuilds the numbered controls using
 * the same `pagination__*` class names the collection page renders from
 * `snippets/pagination-controls.liquid`; the block carries its own port of that
 * snippet's CSS, so the two look identical wherever the gallery is placed.
 *
 * Before this runs, a per-instance `nth-child` rule in the block hides
 * everything past the first page; adding `image-gallery--ready` hands control
 * over to the `hidden` attribute set here.
 *
 * @typedef {object} ImageGalleryRefs
 * @property {HTMLElement} grid - The grid holding one element per image.
 * @property {HTMLElement} [pagination] - The pagination nav (absent when paging is off).
 * @property {HTMLElement} [nextItem] - The `<li>` holding the next arrow; number items are inserted before it.
 * @property {HTMLButtonElement} [prev] - Previous-page arrow.
 * @property {HTMLButtonElement} [next] - Next-page arrow.
 * @property {HTMLElement & { showDialog: () => void, closeDialog: () => void }} [lightbox] - The theme dialog-component wrapping the lightbox.
 *
 * @typedef {object} Slide
 * @property {HTMLElement} item - The grid child the image belongs to.
 * @property {HTMLImageElement} image - The image itself.
 *
 * @extends {Component<ImageGalleryRefs>}
 */
export class ImageGalleryComponent extends Component {
  requiredRefs = ['grid'];

  /** Pages shown either side of the current one before collapsing into an ellipsis. */
  static DESKTOP_WINDOW = 2;

  /** The same window on mobile, where the controls have far less room. */
  static MOBILE_WINDOW = 1;

  #page = 1;

  /** @type {MutationObserver | undefined} */
  #contentObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#page = 1;
    this.#update();
    this.classList.add('image-gallery--ready');

    this.refs.pagination?.addEventListener('click', this.#handleClick);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);

    const { lightbox } = this.refs;

    if (lightbox) {
      this.addEventListener('click', this.#handleZoomClick);
      this.addEventListener('keydown', this.#handleZoomKeydown);
      lightbox.addEventListener('click', this.#handleLightboxClick);
      lightbox.addEventListener('keydown', this.#handleLightboxKeydown);
      lightbox.addEventListener('pointerdown', this.#handlePointerDown);
      lightbox.addEventListener('pointerup', this.#handlePointerUp);
      lightbox.addEventListener('dialog:close', this.#handleLightboxClose);
    }

    // The theme editor adds, removes and reorders image blocks in place. Only
    // childList is observed: the `hidden` and `--ig-i` writes below are
    // attribute changes, so they can't feed back into this.
    this.#contentObserver = new MutationObserver(() => this.#update());
    this.#contentObserver.observe(this.refs.grid, { childList: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.pagination?.removeEventListener('click', this.#handleClick);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#contentObserver?.disconnect();

    const { lightbox } = this.refs;

    if (lightbox) {
      this.removeEventListener('click', this.#handleZoomClick);
      this.removeEventListener('keydown', this.#handleZoomKeydown);
      lightbox.removeEventListener('click', this.#handleLightboxClick);
      lightbox.removeEventListener('keydown', this.#handleLightboxKeydown);
      lightbox.removeEventListener('pointerdown', this.#handlePointerDown);
      lightbox.removeEventListener('pointerup', this.#handlePointerUp);
      lightbox.removeEventListener('dialog:close', this.#handleLightboxClose);
    }
  }

  /** @returns {HTMLElement[]} One element per image in the gallery. */
  get #items() {
    return /** @type {HTMLElement[]} */ ([...this.refs.grid.children]).filter(
      (child) => child instanceof HTMLElement
    );
  }

  /** @returns {number} Images per page, or 0 when paging is switched off. */
  get #perPage() {
    return Math.max(0, Number.parseInt(this.dataset.perPage ?? '0', 10) || 0);
  }

  /**
   * Shows the current page and rebuilds the controls.
   *
   * @param {boolean} [animate] - Fade the page in (set on merchant-driven page changes, not the first paint).
   */
  #update(animate = false) {
    const items = this.#items;
    const perPage = this.#perPage;
    const pages = perPage > 0 ? Math.max(1, Math.ceil(items.length / perPage)) : 1;

    this.#page = Math.min(Math.max(this.#page, 1), pages);

    const start = perPage > 0 ? (this.#page - 1) * perPage : 0;
    const end = perPage > 0 ? start + perPage : items.length;

    items.forEach((item, index) => {
      const visible = index >= start && index < end;

      item.toggleAttribute('hidden', !visible);
      if (visible) item.style.setProperty('--ig-i', String(index - start));
    });

    this.#markZoomable(items);

    if (animate) this.#replayFade();

    this.#renderPagination(pages);
  }

  /** Restarts the stagger animation by removing and re-adding its class. */
  #replayFade() {
    if (!this.classList.contains('image-gallery--fade')) return;

    const { grid } = this.refs;

    grid.classList.remove('image-gallery__grid--enter');
    // Force a reflow so the class re-add counts as a new animation.
    void grid.offsetWidth;
    grid.classList.add('image-gallery__grid--enter');
  }

  /**
   * @param {number} pages - Total number of pages.
   */
  #renderPagination(pages) {
    const { pagination, nextItem, prev, next } = this.refs;

    if (!pagination || !nextItem) return;

    if (pages <= 1) {
      pagination.hidden = true;
      return;
    }

    pagination.hidden = false;
    // Read back by #pageItem to decide which numbers mobile can drop.
    pagination.dataset.pages = String(pages);

    for (const item of pagination.querySelectorAll('[data-ig-part]')) item.remove();

    for (const part of this.#parts(pages)) {
      nextItem.before(part === 'gap' || part === 'gap-mobile' ? this.#gapItem(part) : this.#pageItem(part));
    }

    if (prev) prev.disabled = this.#page === 1;
    if (next) next.disabled = this.#page === pages;
    prev?.classList.toggle('pagination__link--disabled', this.#page === 1);
    next?.classList.toggle('pagination__link--disabled', this.#page === pages);
  }

  /**
   * Builds the sequence of page numbers and ellipses, mirroring the Liquid
   * snippet: a `gap` collapses pages on every screen, a `gap-mobile` only
   * stands in for the numbers that the narrower mobile window drops.
   *
   * @param {number} pages - Total number of pages.
   * @returns {(number | 'gap' | 'gap-mobile')[]} The parts to render, in order.
   */
  #parts(pages) {
    const { DESKTOP_WINDOW, MOBILE_WINDOW } = /** @type {typeof ImageGalleryComponent} */ (this.constructor);
    const current = this.#page;

    /** @type {(number | 'gap' | 'gap-mobile')[]} */
    const parts = [];
    let lastNumber = 0;
    let lastMobileNumber = 0;
    // A shared ellipsis already stands in for the mobile jump too, so mobile
    // must not add a second one right next to it.
    let pendingGap = false;

    for (let page = 1; page <= pages; page++) {
      const edge = page === 1 || page === pages;

      if (!edge && Math.abs(page - current) > DESKTOP_WINDOW) continue;

      if (lastNumber > 0 && page > lastNumber + 1) {
        parts.push('gap');
        pendingGap = true;
      } else if (!pendingGap && lastMobileNumber > 0 && page > lastMobileNumber + 1) {
        // Numbers between these two are on screen for desktop but hidden on
        // mobile, so mobile needs its own ellipsis to bridge the jump.
        parts.push('gap-mobile');
        pendingGap = true;
      }

      parts.push(page);
      lastNumber = page;

      if (edge || Math.abs(page - current) <= MOBILE_WINDOW) {
        lastMobileNumber = page;
        pendingGap = false;
      }
    }

    return parts;
  }

  /**
   * @param {number} page - The page this control jumps to.
   * @returns {HTMLLIElement}
   */
  #pageItem(page) {
    const { MOBILE_WINDOW } = /** @type {typeof ImageGalleryComponent} */ (this.constructor);
    const current = this.#page;
    const item = document.createElement('li');

    item.dataset.igPart = '';
    item.className = 'pagination__item';

    const total = Number.parseInt(this.refs.pagination?.dataset.pages ?? '0', 10);
    if (page !== 1 && page !== total && Math.abs(page - current) > MOBILE_WINDOW) {
      item.classList.add('pagination__item--mobile-hide');
    }

    if (page === current) {
      const label = document.createElement('span');

      label.className = 'pagination__link pagination__link--page pagination__link--current';
      label.setAttribute('aria-current', 'page');
      label.textContent = String(page);
      item.append(label);
    } else {
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'pagination__link pagination__link--page';
      button.dataset.igPage = String(page);
      button.setAttribute('aria-label', (this.dataset.pageLabel ?? 'Page __n__').replace('__n__', String(page)));
      button.textContent = String(page);
      item.append(button);
    }

    return item;
  }

  /**
   * @param {'gap' | 'gap-mobile'} kind - Whether the ellipsis shows everywhere or on mobile only.
   * @returns {HTMLLIElement}
   */
  #gapItem(kind) {
    const item = document.createElement('li');

    item.dataset.igPart = '';
    item.className = `pagination__item pagination__item--gap${
      kind === 'gap-mobile' ? ' pagination__item--mobile-only' : ''
    }`;
    item.innerHTML = '<span class="pagination__link pagination__link--gap" aria-hidden="true">&hellip;</span>';

    return item;
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const control = target.closest('[data-ig-page], [data-ig-step]');

    if (!(control instanceof HTMLElement)) return;

    const page = control.dataset.igPage
      ? Number.parseInt(control.dataset.igPage, 10)
      : this.#page + Number.parseInt(control.dataset.igStep ?? '0', 10);

    this.#goTo(page);
  };

  /** @param {number} page */
  #goTo(page) {
    if (page === this.#page) return;

    this.#page = page;
    this.#update(true);

    if (this.dataset.scroll !== 'true') return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.scrollIntoView({ block: 'start', behavior: reduced ? 'auto' : 'smooth' });
  }

  /* ----------------------------------------------------------------
     Lightbox
     ---------------------------------------------------------------- */

  /** @type {Slide[]} */
  #slides = [];

  #slideIndex = 0;

  /** @type {{ x: number, y: number } | null} */
  #pointerStart = null;

  /**
   * The image a grid item would expand to, or null when it can't expand: an
   * item that carries a link keeps the link (one click can't do both), and a
   * cell with no photo yet — a placeholder in the editor — has nothing to show.
   *
   * @param {HTMLElement} item - A grid child.
   * @returns {HTMLImageElement | null}
   */
  #imageOf(item) {
    if (item.matches('a[href]') || item.querySelector('a[href]')) return null;

    // Prefer the block's main image so a decorative pin can't be mistaken for it.
    const image = item.querySelector('.image-block__image') ?? item.querySelector('img:not(.image-block__pin-img)');

    return image instanceof HTMLImageElement ? image : null;
  }

  /**
   * Flags the items that open in the lightbox and makes them keyboard
   * operable. Re-run on every update so editor edits stay in step.
   *
   * @param {HTMLElement[]} items - Every grid child.
   */
  #markZoomable(items) {
    if (this.dataset.lightbox !== 'true') return;

    let index = 0;

    for (const item of items) {
      const zoomable = this.#imageOf(item) != null;

      item.classList.toggle('image-gallery__zoomable', zoomable);

      if (!zoomable) {
        item.removeAttribute('tabindex');
        item.removeAttribute('role');
        item.removeAttribute('aria-label');
        continue;
      }

      index += 1;
      item.tabIndex = 0;
      item.setAttribute('role', 'button');
      item.setAttribute('aria-label', (this.dataset.zoomLabel ?? 'Zoom media __n__').replace('__n__', String(index)));
    }
  }

  /** @param {MouseEvent} event */
  #handleZoomClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const item = target.closest('.image-gallery__zoomable');

    if (!(item instanceof HTMLElement) || !this.refs.grid.contains(item)) return;

    this.#openLightbox(item);
  };

  /** @param {KeyboardEvent} event */
  #handleZoomKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target;

    if (!(target instanceof HTMLElement) || !target.classList.contains('image-gallery__zoomable')) return;

    event.preventDefault();
    this.#openLightbox(target);
  };

  /**
   * @param {HTMLElement} item - The grid child that was activated.
   */
  #openLightbox(item) {
    // Rebuilt per open so blocks added or reordered since last time are picked
    // up, and so the slides span the whole gallery rather than the page on
    // screen — arrowing past the last image of a page rolls into the next.
    this.#slides = this.#items.flatMap((candidate) => {
      const image = this.#imageOf(candidate);

      return image ? [{ item: candidate, image }] : [];
    });

    const index = this.#slides.findIndex((slide) => slide.item === item);
    const lightbox = this.refs.lightbox;

    if (index < 0 || !lightbox) return;

    this.#showSlide(index);

    // dialog.js is a low-priority module, so on a very early click the element
    // may not have upgraded yet and showDialog wouldn't exist.
    if (typeof lightbox.showDialog === 'function') lightbox.showDialog();
    else customElements.whenDefined('dialog-component').then(() => lightbox.showDialog?.());
  }

  /**
   * @param {number} index - Slide to show; wraps around at either end.
   */
  #showSlide(index) {
    const count = this.#slides.length;

    if (count === 0) return;

    this.#slideIndex = ((index % count) + count) % count;

    const { image } = this.#slides[this.#slideIndex];
    const target = this.#part('image');

    if (target instanceof HTMLImageElement) {
      // Hand the browser the block's own candidate list (up to 2400px) and let
      // it pick for a full-viewport slot rather than the grid cell it was sized for.
      if (image.srcset) target.srcset = image.srcset;
      else target.removeAttribute('srcset');

      target.sizes = '100vw';
      target.src = image.currentSrc || image.src;
      target.alt = image.alt;

      // Intrinsic size, so the box holds its shape while the next slide loads.
      const width = image.naturalWidth || Number(image.getAttribute('width'));
      const height = image.naturalHeight || Number(image.getAttribute('height'));

      if (width && height) {
        target.width = width;
        target.height = height;
      }
    }

    const caption = this.#part('caption');

    if (caption) {
      const text = this.dataset.lightboxCaption === 'true' ? this.#captionOf(this.#slides[this.#slideIndex].item) : '';

      caption.textContent = text;
      caption.hidden = text === '';
    }

    const counter = this.#part('counter');

    if (counter) counter.textContent = `${this.#slideIndex + 1} / ${count}`;

    for (const arrow of this.querySelectorAll('[data-ig-lightbox-step]')) {
      if (arrow instanceof HTMLElement) arrow.hidden = count < 2;
    }
  }

  /**
   * @param {HTMLElement} item - A grid child.
   * @returns {string} The Image block's overlay text, when it has one.
   */
  #captionOf(item) {
    return item.querySelector('.image-block__overlay-text')?.textContent?.trim() ?? '';
  }

  /**
   * @param {string} name - The `data-ig-lightbox` value to look up.
   * @returns {HTMLElement | null}
   */
  #part(name) {
    const element = this.querySelector(`[data-ig-lightbox="${name}"]`);

    return element instanceof HTMLElement ? element : null;
  }

  /** @param {MouseEvent} event */
  #handleLightboxClick = (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const step = target.closest('[data-ig-lightbox-step]');

    if (step instanceof HTMLElement) {
      this.#showSlide(this.#slideIndex + Number.parseInt(step.dataset.igLightboxStep ?? '0', 10));
      return;
    }

    // Anything that isn't the photo or a control — the surrounding dead space —
    // closes, which is what a full-bleed lightbox trains people to expect.
    if (!target.closest('[data-ig-lightbox="image"], [data-ig-lightbox="caption"]')) {
      this.refs.lightbox?.closeDialog();
    }
  };

  /** @param {KeyboardEvent} event */
  #handleLightboxKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    this.#showSlide(this.#slideIndex + (event.key === 'ArrowRight' ? 1 : -1));
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    this.#pointerStart = event.pointerType === 'touch' ? { x: event.clientX, y: event.clientY } : null;
  };

  /** @param {PointerEvent} event */
  #handlePointerUp = (event) => {
    const start = this.#pointerStart;

    this.#pointerStart = null;

    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;

    // Horizontal intent only, and far enough to not be a tap.
    if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;

    this.#showSlide(this.#slideIndex + (dx < 0 ? 1 : -1));
  };

  /**
   * Arrowing can walk off the page that was on screen, so leave the grid
   * showing whichever page holds the image last looked at.
   */
  #handleLightboxClose = () => {
    const slide = this.#slides[this.#slideIndex];
    const perPage = this.#perPage;

    if (!slide || perPage <= 0) return;

    const index = this.#items.indexOf(slide.item);

    if (index < 0) return;

    const page = Math.floor(index / perPage) + 1;

    if (page === this.#page) return;

    this.#page = page;
    this.#update();
  };

  /**
   * Theme editor: selecting an image that lives on another page would otherwise
   * highlight something the merchant can't see, so jump to its page first.
   *
   * @param {Event} event
   */
  #handleBlockSelect = (event) => {
    const target = event.target;
    const perPage = this.#perPage;

    if (perPage <= 0 || !(target instanceof Node) || !this.contains(target)) return;

    const index = this.#items.findIndex((item) => item.contains(target));

    if (index < 0) return;

    this.#goTo(Math.floor(index / perPage) + 1);
  };
}

if (!customElements.get('image-gallery-component')) {
  customElements.define('image-gallery-component', ImageGalleryComponent);
}
